import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Pause, Play, Trash2 } from 'lucide-react';
import { isAbortError } from '@nyce/admin-kit/api';
import { Button, Chip, Select, Switch } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, useAsync, useNotice } from '../../components/common';
import { useParam } from '../../pages/statistics/shared';
import { saveBlob } from '../../buddy/api/client';
import { automationGw, type AgentLogEntry } from '../api/client';
import { useAutomationTarget, useAutomationWrite } from '../shared';
import '../automation.css';

/** Lines kept in the view; older ones drop off. */
export const MAX_LINES = 2000;

const LEVELS = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'];

/** The gateway sends the agent's timestamp as a string, a number or an object — whatever its serializer made of it. */
export function logTime(t: unknown): string {
  const d =
    typeof t === 'string' || typeof t === 'number'
      ? new Date(t)
      : t && typeof t === 'object' && 'dateTime' in t
        ? new Date(String((t as { dateTime: unknown }).dateTime))
        : null;
  return d && Number.isFinite(d.getTime()) ? d.toLocaleTimeString() : '';
}

/** One agent's live log through the gateway, debug logging on demand, and its log files from disk. */
export function AgentLogsPage() {
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const canWrite = useAutomationWrite();
  const toast = useNotice();
  const [agent, setAgent] = useParam('agent', '');
  const [minLevel, setMinLevel] = useParam('level', 'Information');
  const tunnels = useAsync(() => api.tunnels(), [api]);
  const connected = (tunnels.data ?? []).filter((t) => t.isConnected);
  const status = useAsync(() => (agent ? api.logStatus(agent) : Promise.resolve(null)), [api, agent]);
  const [lines, setLines] = useState<(AgentLogEntry & { n: number })[]>([]);
  const [live, setLive] = useState(true);
  const [state, setState] = useState<'idle' | 'connecting' | 'streaming' | 'ended'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const counter = useRef(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLines([]);
    setError(null);
    if (!agent || !live) {
      setState('idle');
      return;
    }
    const ctl = new AbortController();
    setState('connecting');
    api
      .streamLogs(
        agent,
        (e) => {
          if (e.event === 'connected') setState('streaming');
          else if (e.event === 'end') {
            setState('ended');
            setError(e.data.reason);
          } else if (e.event === 'log')
            setLines((cur) => [...cur.slice(-(MAX_LINES - 1)), { ...e.data, n: ++counter.current }]);
        },
        ctl.signal
      )
      .then(
        () => !ctl.signal.aborted && setState('ended'),
        (e: unknown) => {
          if (isAbortError(e)) return;
          setState('ended');
          setError((e as Error).message);
        }
      );
    return () => ctl.abort();
  }, [api, agent, live]);

  useEffect(() => {
    const el = box.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 80) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const min = LEVELS.indexOf(minLevel);
  const shown = lines.filter((l) => {
    const i = LEVELS.indexOf(l.level);
    return i < 0 || i >= min;
  });

  const setDebug = async (on: boolean) => {
    try {
      await api.setDebug(agent, on);
      await status.reload();
      toast.success(`Debug logging ${on ? 'on' : 'off'}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const download = async () => {
    setDownloading(true);
    try {
      const f = await api.downloadLogs(agent);
      saveBlob(f.blob, f.fileName);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Page
      title="Agent logs"
      description="An agent's log as it happens, relayed by the gateway while this page is open. The log files on the agent's disk download whole."
      actions={
        agent && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Download size={14} />}
            loading={downloading}
            onClick={() => void download()}
          >
            Log files
          </Button>
        )
      }
    >
      {toast.element}
      <div className="ag-toolbar">
        <Select
          aria-label="Agent"
          value={agent}
          onChange={setAgent}
          options={[
            { value: '', label: `Pick a connected agent (${connected.length})` },
            ...connected
              .slice()
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((t) => ({
                value: t.clientId,
                label: `${t.displayName} (${t.clientId}) · ${t.tenantName}`,
              })),
            // an agent from a link that is offline now still shows as picked
            ...(agent && !connected.some((t) => t.clientId === agent)
              ? [{ value: agent, label: `${agent} (offline)` }]
              : []),
          ]}
        />
        <Select
          aria-label="Level"
          value={minLevel}
          onChange={setMinLevel}
          options={LEVELS.map((l) => ({ value: l, label: `${l} and above` }))}
        />
        {agent && (
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={live ? <Pause size={14} /> : <Play size={14} />}
              onClick={() => setLive(!live)}
            >
              {live ? 'Stop' : 'Follow'}
            </Button>
            <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} onClick={() => setLines([])}>
              Clear
            </Button>
            {canWrite && status.data && (
              <Switch
                checked={status.data.debugLoggingEnabled}
                onChange={(v) => void setDebug(v)}
                label="Debug logging"
              />
            )}
            <Chip size="sm" data-state={state}>
              {state === 'streaming' ? 'live' : state}
            </Chip>
          </>
        )}
      </div>
      <ErrorBox error={tunnels.error || error} />
      <Loading loading={tunnels.loading && !tunnels.data} />
      {!agent && tunnels.data && <EmptyState card title="Pick an agent to follow its log." />}
      {agent && (
        <div className="ag-log" ref={box} data-testid="ag-log" aria-live="off">
          {!shown.length && (
            <p className="nyk-ui-hint">{state === 'streaming' ? 'Waiting for log lines…' : 'No lines.'}</p>
          )}
          {shown.map((l) => (
            <div key={l.n} className="ag-log-line" data-level={l.level}>
              <span className="ag-log-meta">
                {logTime(l.timestamp)} {l.level.slice(0, 4).toUpperCase()} {l.category}
              </span>{' '}
              {l.message}
              {l.exception && `\n${l.exception}`}
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
